import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';

type ContentTab = 'general' | 'kb' | 'email' | 'document';

interface GenerateResponse {
  content: string;
  type?: string;
  model: string;
  cost_usd: number;
  usage: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
  document_id?: string;
  stored?: boolean;
}

export function Content() {
  const [activeTab, setActiveTab] = useState<ContentTab>('general');

  // General generation form
  const [genType, setGenType] = useState('proposal');
  const [genPrompt, setGenPrompt] = useState('');
  const [genContext, setGenContext] = useState('');
  const [genModel, setGenModel] = useState('gpt-4.1-mini');

  // KB form
  const [kbTopic, setKbTopic] = useState('');
  const [kbAudience, setKbAudience] = useState('technician');
  const [kbSourceType, setKbSourceType] = useState('general');

  // Email form
  const [emailMode, setEmailMode] = useState<'response' | 'template'>('response');
  const [emailResponseType, setEmailResponseType] = useState('acknowledge');
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailTemplateType, setEmailTemplateType] = useState('ticket_acknowledgement');
  const [emailUseCase, setEmailUseCase] = useState('');

  // Document form
  const [docType, setDocType] = useState('proposal');
  const [docCompany, setDocCompany] = useState('');
  const [docTitle, setDocTitle] = useState('');
  const [docContext, setDocContext] = useState('');

  // Result
  const [result, setResult] = useState<string | null>(null);

  const generalMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<GenerateResponse>('content/generate', { body }),
    onSuccess: (data) => setResult(data.content),
  });

  const kbMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<GenerateResponse>('content/generate/kb', { body }),
    onSuccess: (data) => setResult(data.content),
  });

  const emailMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Record<string, unknown>>('content/generate/email', { body }),
    onSuccess: (data) => {
      if (data.body) {
        setResult(`Subject: ${data.subject ?? '(no subject)'}\n\n${data.body}`);
      } else if (data.body_template) {
        setResult(`Template: ${data.template_name}\nSubject: ${data.subject_template}\n\n${data.body_template}`);
      } else {
        setResult(JSON.stringify(data, null, 2));
      }
    },
  });

  const docMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<GenerateResponse>(`content/documents/${body.docEndpoint}`, { body: body.payload }),
    onSuccess: (data) => setResult(data.content),
  });

  const isGenerating = generalMutation.isPending || kbMutation.isPending || emailMutation.isPending || docMutation.isPending;

  const handleGenerate = () => {
    setResult(null);
    switch (activeTab) {
      case 'general':
        if (!genPrompt.trim()) return;
        generalMutation.mutate({ type: genType, prompt: genPrompt, context: genContext || undefined, model: genModel });
        break;
      case 'kb':
        if (!kbTopic.trim()) return;
        kbMutation.mutate({ topic: kbTopic, target_audience: kbAudience, source_type: kbSourceType });
        break;
      case 'email':
        if (emailMode === 'response') {
          emailMutation.mutate({ mode: 'response', response_type: emailResponseType, recipient_name: emailRecipient || undefined, subject_context: emailSubject || undefined });
        } else {
          if (!emailUseCase.trim()) return;
          emailMutation.mutate({ mode: 'template', template_type: emailTemplateType, use_case_description: emailUseCase });
        }
        break;
      case 'document':
        if (docType === 'proposal') {
          if (!docCompany.trim()) return;
          docMutation.mutate({ docEndpoint: 'proposal', payload: { company_name: docCompany, services: ['Managed IT'], custom_context: docContext || undefined } });
        } else {
          if (!docTitle.trim()) return;
          docMutation.mutate({ docEndpoint: docType, payload: { title: docTitle, context_description: docContext || 'General' } });
        }
        break;
    }
  };

  const tabs: { id: ContentTab; label: string }[] = [
    { id: 'general', label: 'General' },
    { id: 'kb', label: 'KB Articles' },
    { id: 'email', label: 'Email' },
    { id: 'document', label: 'Documents' },
  ];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground mb-1">Content Generation</h1>
        <p className="text-muted-foreground">AI-powered content creation for proposals, KB articles, emails, and documents.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setResult(null); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="rounded-lg border bg-card p-6">
          {activeTab === 'general' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Content Type</label>
                  <select value={genType} onChange={(e) => setGenType(e.target.value)} className="w-full border rounded-md px-3 py-2 bg-background text-sm">
                    <option value="proposal">Proposal</option>
                    <option value="battlecard">Battlecard</option>
                    <option value="report">Report</option>
                    <option value="email">Email</option>
                    <option value="documentation">Documentation</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Model</label>
                  <select value={genModel} onChange={(e) => setGenModel(e.target.value)} className="w-full border rounded-md px-3 py-2 bg-background text-sm">
                    <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                    <option value="gpt-4.1">GPT-4.1</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Prompt</label>
                <textarea value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)} placeholder="Describe what you want to generate..." rows={3} className="w-full border rounded-md px-3 py-2 bg-background text-sm resize-none" />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Context (optional)</label>
                <textarea value={genContext} onChange={(e) => setGenContext(e.target.value)} placeholder="Additional context..." rows={2} className="w-full border rounded-md px-3 py-2 bg-background text-sm resize-none" />
              </div>
            </div>
          )}

          {activeTab === 'kb' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Topic</label>
                <input type="text" value={kbTopic} onChange={(e) => setKbTopic(e.target.value)} placeholder="e.g. Setting up MFA in Microsoft 365" className="w-full border rounded-md px-3 py-2 bg-background text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Audience</label>
                  <select value={kbAudience} onChange={(e) => setKbAudience(e.target.value)} className="w-full border rounded-md px-3 py-2 bg-background text-sm">
                    <option value="end_user">End User</option>
                    <option value="technician">Technician</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Source Type</label>
                  <select value={kbSourceType} onChange={(e) => setKbSourceType(e.target.value)} className="w-full border rounded-md px-3 py-2 bg-background text-sm">
                    <option value="general">General</option>
                    <option value="ticket">From Ticket</option>
                    <option value="incident">From Incident</option>
                    <option value="gap">Knowledge Gap</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'email' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Mode</label>
                <div className="flex gap-2">
                  {(['response', 'template'] as const).map((m) => (
                    <button key={m} onClick={() => setEmailMode(m)} className={`px-3 py-1.5 rounded-md text-sm capitalize ${emailMode === m ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              {emailMode === 'response' ? (
                <>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Response Type</label>
                    <select value={emailResponseType} onChange={(e) => setEmailResponseType(e.target.value)} className="w-full border rounded-md px-3 py-2 bg-background text-sm">
                      <option value="acknowledge">Acknowledge</option>
                      <option value="update">Update</option>
                      <option value="resolve">Resolve</option>
                      <option value="followup">Follow-up</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Recipient</label>
                    <input type="text" value={emailRecipient} onChange={(e) => setEmailRecipient(e.target.value)} placeholder="Customer name" className="w-full border rounded-md px-3 py-2 bg-background text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Subject Context</label>
                    <input type="text" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="What is this email about?" className="w-full border rounded-md px-3 py-2 bg-background text-sm" />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Template Type</label>
                    <select value={emailTemplateType} onChange={(e) => setEmailTemplateType(e.target.value)} className="w-full border rounded-md px-3 py-2 bg-background text-sm">
                      <option value="ticket_acknowledgement">Ticket Acknowledgement</option>
                      <option value="ticket_update">Ticket Update</option>
                      <option value="ticket_resolution">Ticket Resolution</option>
                      <option value="contract_renewal">Contract Renewal</option>
                      <option value="welcome_new_client">Welcome New Client</option>
                      <option value="maintenance_notification">Maintenance Notification</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Use Case Description</label>
                    <textarea value={emailUseCase} onChange={(e) => setEmailUseCase(e.target.value)} placeholder="Describe how this template will be used..." rows={2} className="w-full border rounded-md px-3 py-2 bg-background text-sm resize-none" />
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'document' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Document Type</label>
                <select value={docType} onChange={(e) => setDocType(e.target.value)} className="w-full border rounded-md px-3 py-2 bg-background text-sm">
                  <option value="proposal">Sales Proposal</option>
                  <option value="sop">SOP</option>
                  <option value="rca">Root Cause Analysis</option>
                  <option value="qbr">QBR Report</option>
                  <option value="policy">Policy Document</option>
                </select>
              </div>
              {docType === 'proposal' ? (
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Company Name</label>
                  <input type="text" value={docCompany} onChange={(e) => setDocCompany(e.target.value)} placeholder="Prospect company name" className="w-full border rounded-md px-3 py-2 bg-background text-sm" />
                </div>
              ) : (
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Title</label>
                  <input type="text" value={docTitle} onChange={(e) => setDocTitle(e.target.value)} placeholder="Document title" className="w-full border rounded-md px-3 py-2 bg-background text-sm" />
                </div>
              )}
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Context</label>
                <textarea value={docContext} onChange={(e) => setDocContext(e.target.value)} placeholder="Provide context for the document..." rows={3} className="w-full border rounded-md px-3 py-2 bg-background text-sm resize-none" />
              </div>
            </div>
          )}

          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="mt-4 w-full bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2.5 rounded-md text-sm font-medium disabled:opacity-50"
          >
            {isGenerating ? 'Generating...' : 'Generate'}
          </button>

          {(generalMutation.isError || kbMutation.isError || emailMutation.isError || docMutation.isError) && (
            <p className="mt-2 text-sm text-red-500">
              Generation failed. Please try again.
            </p>
          )}
        </div>

        {/* Result */}
        <div className="rounded-lg border bg-card p-6">
          <h3 className="text-sm font-medium mb-3">Generated Content</h3>
          {result ? (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-foreground bg-muted/50 rounded-lg p-4 overflow-auto max-h-[600px]">
                {result}
              </pre>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(result);
                }}
                className="mt-3 px-3 py-1.5 text-xs border rounded-md hover:bg-muted"
              >
                Copy to clipboard
              </button>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-12">
              Generated content will appear here.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
